import { useEffect } from 'react';
import type { LegalAction } from '../api-client.js';
import type { HandCardData } from './HandCard.js';

export type CardActionPopoverProps = {
  card: HandCardData;
  actions: LegalAction[];
  busy: boolean;
  onPick: (action: LegalAction) => void;
  onPreview: () => void;
  onClose: () => void;
};

/**
 * Centred action chooser. Surfaces when a card has more than one legal
 * descriptor it can participate in (e.g. a player + multiple action
 * cards in hand → cartesian product of plays). Closes on Esc, on
 * backdrop click, or after picking.
 */
export function CardActionPopover({
  card,
  actions,
  busy,
  onPick,
  onPreview,
  onClose,
}: CardActionPopoverProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="ec-surface w-full max-w-sm p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-baseline justify-between border-b border-ec-line pb-2">
          <h3 className="font-display text-sm font-medium text-ec-face">
            {card.name ?? 'Card'}{' '}
            <span className="text-xs font-normal text-ec-bone-dim">
              · choose an action
            </span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-ec-bone-dim hover:text-ec-face"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <ul className="max-h-72 space-y-1.5 overflow-y-auto">
          {actions.map((action) => (
            <li key={action.descriptorId}>
              <button
                type="button"
                onClick={() => onPick(action)}
                disabled={busy}
                className="ec-btn w-full !justify-start text-sm"
              >
                {action.label}
              </button>
            </li>
          ))}
        </ul>

        <footer className="mt-3 flex justify-between border-t border-ec-line pt-2 text-xs">
          <button
            type="button"
            onClick={onPreview}
            className="text-ec-brass hover:underline"
          >
            Preview card
          </button>
          <span className="text-ec-bone-dim">Esc to close</span>
        </footer>
      </div>
    </div>
  );
}
